import { useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Tooltip from '@mui/material/Tooltip'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import { getBrowserLocation } from '../../services/locationService'
import { useDiveStore } from '../../stores/diveStore'

export type LocationRefreshButtonProps = {
  onLocationRefresh: (location: { lat: number; lng: number }) => void
}

const LocationRefreshButton = ({
  onLocationRefresh
}: LocationRefreshButtonProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const setReferenceLocation = useDiveStore(state => state.setReferenceLocation)

  const handleClick = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const location = await getBrowserLocation()
      setReferenceLocation(location)
      onLocationRefresh(location)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to get current location'

      if (
        message.includes('permission denied') ||
        message.includes('not supported') ||
        message.includes('Position unavailable')
      ) {
        setError('Live location is unavailable')
      } else {
        setError(message)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box>
      <Tooltip title='Use my current location'>
        <IconButton
          onClick={handleClick}
          disabled={isLoading}
          aria-label='Use my current location'
          color='primary'
        >
          {isLoading ? <CircularProgress size={22} /> : <MyLocationIcon />}
        </IconButton>
      </Tooltip>
      {error && (
        <Alert severity='warning' sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  )
}

export default LocationRefreshButton
